import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

export const fetchAdminModerationCases = createAsyncThunk(
  "adminModeration/fetchCases",
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 0,
        limit = 10,
        searchTerm = "",
        typeFilter = "all",
        priorityFilter = "all",
        statusFilter = "all",
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: Math.min(limit, 50).toString(), // Cap at 50 for performance
        ...(searchTerm && { search: searchTerm }),
        ...(typeFilter !== "all" && { type: typeFilter }),
        ...(priorityFilter !== "all" && { priority: priorityFilter }),
        ...(statusFilter !== "all" && { status: statusFilter }),
      });

      const response = await fetch(`/api/admin/moderation?${queryParams}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache", // Prevent stale data
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch cases");
      }

      const data = await response.json();
      return {
        ...data,
        requestParams: params, // Store request params for cache invalidation
        timestamp: Date.now(),
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Fetch detailed case data with caching
export const fetchCaseDetails = createAsyncThunk(
  "adminModeration/fetchCaseDetails",
  async (caseId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const cachedCase = state.adminModeration.caseDetails[caseId];

      // Return cached data if it's fresh (less than 5 minutes old)
      if (cachedCase && Date.now() - cachedCase.timestamp < 300000) {
        return cachedCase;
      }

      const response = await fetch(`/api/admin/moderation/${caseId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch case details");
      }

      const data = await response.json();
      return {
        ...data.case,
        timestamp: Date.now(),
        cacheKey: caseId,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const resolveModerationCase = createAsyncThunk(
  "adminModeration/resolveCase",
  async (
    { caseId, resolution, notes },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      dispatch(
        updateCaseOptimistically({
          caseId,
          updates: {
            status: "resolved",
            resolution,
            resolved_at: new Date().toISOString(),
            adminNotes: notes,
          },
        }),
      );

      const response = await fetch("/api/admin/moderation/resolve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caseId, resolution, notes }),
      });

      if (!response.ok) {
        dispatch(revertOptimisticUpdate({ caseId }));
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to resolve case");
      }

      const data = await response.json();

      dispatch(invalidateCaseCache({ caseId }));

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const assignModerationCase = createAsyncThunk(
  "adminModeration/assignCase",
  async ({ caseId, adminId, notes }, { rejectWithValue }) => {
    try {
      const response = await fetch("/api/admin/moderation/assign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caseId, adminId, notes }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to assign case");
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteModerationCase = createAsyncThunk(
  "adminModeration/deleteCase",
  async (caseId, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(removeCaseOptimistically({ caseId }));

      const response = await fetch("/api/admin/moderation/delete", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caseId }),
      });

      if (!response.ok) {
        dispatch(revertCaseRemoval({ caseId }));
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete case");
      }

      const data = await response.json();

      dispatch(invalidateCaseCache({ caseId }));

      return { caseId, ...data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const reEnrichCase = createAsyncThunk(
  "adminModeration/reEnrichCase",
  async (
    { caseId, correctedUrl, adminNotes, forceApprove },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await fetch("/api/admin/moderation/re-enrich", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          caseId,
          correctedUrl,
          adminNotes,
          forceApprove,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      // Refresh case details after successful re-enrichment
      dispatch(fetchCaseDetails(caseId));

      return { caseId, ...data };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to re-enrich case",
      });
    }
  },
);

// Verify a user's email via Hunter (stateless, does not write to Firestore).
// Used by the moderation UI before the admin clicks Send.
export const verifyUserEmail = createAsyncThunk(
  "adminModeration/verifyUserEmail",
  async (payload, { rejectWithValue }) => {
    try {
      const { email, caseId } =
        typeof payload === "string" ? { email: payload } : payload || {};
      const response = await fetch("/api/admin/moderation/verify-user-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, caseId }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to verify email",
      });
    }
  },
);

// Process a user's pending booking request (admin-triggered, server-side)
export const processBooking = createAsyncThunk(
  "adminModeration/processBooking",
  async ({ caseId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetch("/api/admin/moderation/process-booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caseId }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return rejectWithValue(data);
      }

      // Refresh case details so the UI reflects the logged booking
      dispatch(fetchCaseDetails(caseId));

      return {
        caseId,
        isPaidInstant: data.isPaidInstant || false,
        requiresPaymentVerification: data.requiresPaymentVerification || false,
        ...data,
      };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to process booking",
      });
    }
  },
);

// Send notification email to user
export const notifyUser = createAsyncThunk(
  "adminModeration/notifyUser",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      // Backwards-compat: callers may pass either a bare caseId string or
      // an object { caseId, emailOverride, verification }.
      const { caseId, emailOverride, verification } =
        typeof payload === "string" ? { caseId: payload } : payload || {};

      const response = await fetch("/api/admin/moderation/notify-user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ caseId, emailOverride, verification }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      // Refresh case details after notification
      dispatch(fetchCaseDetails(caseId));

      return { caseId, ...data };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to send notification",
      });
    }
  },
);

const initialState = {
  cases: [],
  caseDetails: {},
  loading: false,
  detailsLoading: {},
  error: null,
  success: null,
  searchTerm: "",
  typeFilter: "all",
  priorityFilter: "all",
  statusFilter: "all",
  page: 0,
  rowsPerPage: 10,
  totalCount: 0,
  hasMore: false, // For cursor-based pagination
  expandedCaseId: null,
  lastFetch: null, // For cache invalidation
  optimisticUpdates: {}, // Track optimistic updates
  initialFetchCompleted: false, // Track if initial fetch has been attempted
  summary: {
    pending: 0,
    urgent: 0,
    inReview: 0,
    resolved: 0,
    byType: {
      email_not_verified: 0,
      delivery_failed: 0,
      no_email: 0,
      linkedin_validation: 0,
      unsubscribed_email: 0,
      unverified_signup_email: 0,
    },
  },
};

const adminModerationSlice = createSlice({
  name: "adminModeration",
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setTypeFilter: (state, action) => {
      state.typeFilter = action.payload;
    },
    setPriorityFilter: (state, action) => {
      state.priorityFilter = action.payload;
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setExpandedCaseId: (state, action) => {
      state.expandedCaseId = action.payload;
    },

    setInitialFetchCompleted: (state, action) => {
      state.initialFetchCompleted = action.payload;
    },

    setCaseDetails: (state, action) => {
      const { caseId, details } = action.payload;
      state.caseDetails[caseId] = {
        ...details,
        timestamp: Date.now(),
      };
    },
    invalidateCaseCache: (state, action) => {
      const { caseId } = action.payload;
      delete state.caseDetails[caseId];
    },
    clearAllCache: (state) => {
      state.caseDetails = {};
    },

    // Optimistic updates
    updateCaseOptimistically: (state, action) => {
      const { caseId, updates } = action.payload;

      const caseIndex = state.cases.findIndex((c) => c.id === caseId);
      if (caseIndex !== -1) {
        state.cases[caseIndex] = { ...state.cases[caseIndex], ...updates };
      }

      if (state.caseDetails[caseId]) {
        state.caseDetails[caseId] = {
          ...state.caseDetails[caseId],
          ...updates,
        };
      }

      state.optimisticUpdates[caseId] = {
        originalCase: state.cases[caseIndex],
        updates,
      };
    },
    revertOptimisticUpdate: (state, action) => {
      const { caseId } = action.payload;
      const optimisticUpdate = state.optimisticUpdates[caseId];

      if (optimisticUpdate) {
        const caseIndex = state.cases.findIndex((c) => c.id === caseId);
        if (caseIndex !== -1) {
          state.cases[caseIndex] = optimisticUpdate.originalCase;
        }
        delete state.optimisticUpdates[caseId];
      }
    },
    removeCaseOptimistically: (state, action) => {
      const { caseId } = action.payload;
      const caseIndex = state.cases.findIndex((c) => c.id === caseId);

      if (caseIndex !== -1) {
        state.optimisticUpdates[caseId] = {
          action: "remove",
          originalCase: state.cases[caseIndex],
          originalIndex: caseIndex,
        };

        state.cases.splice(caseIndex, 1);
        state.totalCount = Math.max(0, state.totalCount - 1);
      }
    },

    revertCaseRemoval: (state, action) => {
      const { caseId } = action.payload;
      const optimisticUpdate = state.optimisticUpdates[caseId];

      if (optimisticUpdate && optimisticUpdate.action === "remove") {
        state.cases.splice(
          optimisticUpdate.originalIndex,
          0,
          optimisticUpdate.originalCase,
        );
        state.totalCount += 1;
        delete state.optimisticUpdates[caseId];
      }
    },

    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminModerationCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminModerationCases.fulfilled, (state, action) => {
        state.loading = false;
        state.cases = action.payload.cases;
        state.totalCount = action.payload.totalCount;
        state.hasMore = action.payload.hasMore;
        state.summary = action.payload.summary;
        state.lastFetch = action.payload.timestamp;
        state.error = action.payload.error || null;
        state.initialFetchCompleted = true;
      })
      .addCase(fetchAdminModerationCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.initialFetchCompleted = true;
      })

      .addCase(fetchCaseDetails.pending, (state, action) => {
        const caseId = action.meta.arg;
        state.detailsLoading[caseId] = true;
      })
      .addCase(fetchCaseDetails.fulfilled, (state, action) => {
        const caseData = action.payload;
        const caseId = caseData.cacheKey || caseData.id;

        state.caseDetails[caseId] = caseData;
        state.detailsLoading[caseId] = false;
      })
      .addCase(fetchCaseDetails.rejected, (state, action) => {
        const caseId = action.meta.arg;
        state.detailsLoading[caseId] = false;
        state.error = action.payload;
      })

      .addCase(deleteModerationCase.pending, (state, action) => {
        // Optional: add loading state for delete if needed
      })
      .addCase(deleteModerationCase.fulfilled, (state, action) => {
        const { caseId } = action.payload;
        state.success = "Case deleted successfully";

        delete state.optimisticUpdates[caseId];

        delete state.caseDetails[caseId];

        if (state.expandedCaseId === caseId) {
          state.expandedCaseId = null;
        }
      })
      .addCase(deleteModerationCase.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(resolveModerationCase.fulfilled, (state, action) => {
        state.success = "Case resolved successfully";
        const caseId = action.meta.arg.caseId;
        delete state.optimisticUpdates[caseId];
      })
      .addCase(resolveModerationCase.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(assignModerationCase.fulfilled, (state, action) => {
        state.success = "Case assigned successfully";
      })
      .addCase(assignModerationCase.rejected, (state, action) => {
        state.error = action.payload;
      });

    builder.addCase(reEnrichCase.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(reEnrichCase.fulfilled, (state, action) => {
      state.loading = false;
      state.success = action.payload.message || "Case re-enriched successfully";

      const resolution = action.payload.data?.forceApproved
        ? "admin_force_approved"
        : "admin_corrected";

      // Update case in list if present
      const caseIndex = state.cases.findIndex(
        (c) => c.id === action.payload.caseId,
      );
      if (caseIndex !== -1) {
        state.cases[caseIndex].status = "resolved";
        state.cases[caseIndex].resolution = resolution;
      }

      // Update case details cache
      if (state.caseDetails[action.payload.caseId]) {
        state.caseDetails[action.payload.caseId] = {
          ...state.caseDetails[action.payload.caseId],
          status: "resolved",
          resolution: resolution,
          correctedUrl: action.payload.data?.correctedUrl,
        };
      }
    });
    builder.addCase(reEnrichCase.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to re-enrich case";
    });

    // Process pending booking
    builder.addCase(processBooking.pending, (state) => {
      state.error = null;
    });
    builder.addCase(processBooking.fulfilled, (state, action) => {
      state.success = "Booking request processed successfully";
      // Mark booking as processed in case details cache
      if (state.caseDetails[action.payload.caseId]) {
        state.caseDetails[action.payload.caseId] = {
          ...state.caseDetails[action.payload.caseId],
          bookingProcessedByAdmin: true,
          userData: {
            ...state.caseDetails[action.payload.caseId].userData,
            pendingRequest: null,
          },
        };
      }
    });
    builder.addCase(processBooking.rejected, (state, action) => {
      state.error = action.payload?.message || "Failed to process booking";
    });

    // Notify user
    builder.addCase(notifyUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(notifyUser.fulfilled, (state, action) => {
      state.loading = false;
      state.success = action.payload.message || "User notified successfully";

      // Update case notification status
      const caseIndex = state.cases.findIndex(
        (c) => c.id === action.payload.caseId,
      );
      if (caseIndex !== -1) {
        state.cases[caseIndex].userNotified = true;
      }

      // Update case details cache
      if (state.caseDetails[action.payload.caseId]) {
        state.caseDetails[action.payload.caseId] = {
          ...state.caseDetails[action.payload.caseId],
          userNotified: true,
          notifiedAt: new Date().toISOString(),
        };
      }
    });
    builder.addCase(notifyUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to send notification";
    });
  },
});

export const {
  setSearchTerm,
  setTypeFilter,
  setPriorityFilter,
  setStatusFilter,
  setPage,
  setRowsPerPage,
  setExpandedCaseId,
  setCaseDetails,
  invalidateCaseCache,
  clearAllCache,
  updateCaseOptimistically,
  revertOptimisticUpdate,
  clearError,
  clearSuccess,
  removeCaseOptimistically,
  revertCaseRemoval,
  setInitialFetchCompleted,
} = adminModerationSlice.actions;

export default adminModerationSlice.reducer;
